import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';

/**
 * iOS-style Action Sheet for mobile
 * Shows options in a bottom sheet format with drag-to-close
 * Uses portal to render above all other content
 */
export default function ActionSheet({
  isOpen,
  onClose,
  title,
  options = [], // { label, value, icon?, selected? }
  onSelect,
}) {
  const dragControls = useDragControls();

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 9998,
            }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 400,
            }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              // Close if dragged down fast enough or past threshold
              if (info.velocity.y > 500 || info.offset.y > 100) {
                onClose();
              }
            }}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              maxHeight: '70vh',
              background: '#1a1a1a',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              zIndex: 9999,
              paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
              boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Drag indicator - triggers drag */}
            <div 
              onPointerDown={(e) => dragControls.start(e)}
              style={{ 
                padding: '10px 0 6px', 
                display: 'flex', 
                justifyContent: 'center',
                flexShrink: 0,
                cursor: 'grab',
                touchAction: 'none',
              }}
            >
              <div style={{
                width: 32,
                height: 4,
                borderRadius: 2,
                background: 'rgba(255,255,255,0.2)',
              }} />
            </div>

            {/* Title - sticky */}
            {title && (
              <div style={{
                padding: '8px 20px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: '#9ca3af',
                textAlign: 'center',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                flexShrink: 0,
              }}>
                {title}
              </div>
            )}

            {/* Options - scrollable */}
            <div 
              className="actionsheet-options"
              style={{ 
                padding: '0 12px', 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                minHeight: 0,
                // Hide scrollbar
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <style>{`
                .actionsheet-options::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {options.map((option, idx) => (
                <motion.button
                  key={option.value ?? idx}
                  whileTap={{ scale: 0.98, backgroundColor: 'rgba(255,255,255,0.1)' }}
                  onClick={() => {
                    onSelect(option.value ?? option);
                    onClose();
                  }}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    marginBottom: 4,
                    background: option.selected ? 'rgba(250, 139, 20, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: option.selected ? '1px solid rgba(250, 139, 20, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                    color: option.selected ? '#FA8B14' : '#fff',
                  }}
                >
                  {option.icon && (
                    <span style={{ fontSize: 18 }}>{option.icon}</span>
                  )}
                  <span style={{ 
                    flex: 1, 
                    textAlign: 'left', 
                    fontSize: 15,
                    fontWeight: option.selected ? 600 : 400,
                  }}>
                    {option.label}
                  </span>
                  {option.selected && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </motion.button>
              ))}
            </div>

            {/* Cancel button - sticky at bottom */}
            <div style={{ 
              padding: '8px 12px 0', 
              flexShrink: 0,
              background: '#1a1a1a', // Match background
            }}>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 10,
                  color: '#9ca3af',
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </motion.button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
